import React from 'react';
import PropTypes from 'prop-types';
import {
  Field,
  reduxForm,
  formValueSelector,
  isDirty,
  stopSubmit,
  FieldArray,
} from './finalForm';
import LinkContainer from '../components/LinkContainer';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Prompt from '../routes/Prompt';

import ErrorMessage from '../components/ErrorMessage';
import renderField from './render/renderField';
import renderSelect from './render/renderSelect';
import renderFileField from './render/renderFileField';
import renderQuestionsField from './render/renderQuestionsField';
import normalizePhone from './normalize';
import {
  email,
  matchPattern,
  maxLength,
  phone,
  required,
  resume,
} from './validators';
import renderCheckbox from './render/renderCheckbox';


const maxLength10000 = maxLength(10000);
const maxLength200 = maxLength(200);
const matchPatternNumber = matchPattern(/^[ 0-9]+$/, 'Можно вводить только числа');

class CreateResponseForm extends React.Component {
  constructor(props) {
    super(props);
    const { isAllVacancyPage, vacancies } = this.props;
    this.state = {
      vacancy: isAllVacancyPage ? { ...vacancies[0] } : null,
    };
  }

  onChangeVacancy = (event) => {
    this.setState({ vacancy: event.value });
  };

  validateResponseForm = (fieldType) => {
    switch (fieldType) {
      case 'tel': return phone;
      case 'email': return email;
      case 'textarea': return maxLength10000;
      case 'file': return resume;
      case 'number': return matchPatternNumber;
      default: return maxLength200;
    }
  };

  getFields = (vacancy) => {
    const { fields } = vacancy;

    const result = [];
    fields.forEach((field) => {
      result.push((
        <Field
          key={field.name}
          type={field.type}
          name={field.type === 'file' ? `userFile_${field.name}` : field.name}
          component={field.type !== 'file' ? renderField : renderFileField}
          label={field.type !== 'file' ? field.placeholder
            : `Прикрепить ${field.placeholder && field.placeholder.toLowerCase()}`}
          validate={field.required ? [this.validateResponseForm(field.type), required]
            : this.validateResponseForm(field.type)}
          rows="10"
          normalize={field.name === 'phone' ? normalizePhone : undefined}
          accept={field.type === 'file' ? '.pdf, .doc, .docx' : undefined}
          info={field.type === 'file' ? 'Допустимы файлы в формате .pdf, .doc, .docx, размером не более 10 МБ.'
            : undefined}
        />
      ));
    });

    return result;
  };

  render() {
    const {
      handleSubmit,
      submitting,
      dirty,
      submitSucceeded,
      submitFailed,
      activeTeamShortId,
      vacancies,
      questions,
      isAllVacancyPage,
      sources,
      policyUrl,
    } = this.props;
    const { vacancy } = this.state;

    const cancelUrl = isAllVacancyPage ? '/all-vacancies'
      : `/teams/${activeTeamShortId}/vacancies/responses`;

    const activeVacancies = vacancies.filter(({ hidden }) => !hidden);
    const sourcesOptions = sources.map(({ id: value, name: label }) => ({ label, value }));

    return (
      <form onSubmit={handleSubmit} encType="multipart/form-data" className="createResponse">
        <Prompt
          when={dirty && !submitSucceeded}
          message="Изменения отклика не сохранены. Вы уверены, что хотите покинуть страницу?"
        />
        {!isAllVacancyPage && (
          <div className="response-form__field">
            <Field
              name="vacancy"
              key="vacancy"
              component={renderSelect}
              label="Вакансия"
              options={activeVacancies.map(vacancy => ({ label: vacancy.title, value: vacancy }))}
              onChange={this.onChangeVacancy}
              defaultValue={vacancy}
            />
          </div>
        )}
        {(vacancy) && (
          <React.Fragment>
            <div className="response-form__field">
              {this.getFields(vacancy)}
            </div>

            <FieldArray
              name="questions"
              component={renderQuestionsField}
              vacancy={vacancy}
              questions={questions}
            />
            <div className="response-form__field">
              <Field
                name="source"
                component={renderSelect}
                label="Источник"
                options={sourcesOptions}
                defaultValue={sourcesOptions[0]}
                isSearchable={false}
              />
            </div>
            <div className="response-form__field">
              <Field
                name="acceptRights"
                type="checkbox"
                label={
                  (
                    <a
                      className="hrs-check-box-confirmation-link"
                      href={policyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Я даю своё согласие на обработку персональных данных
                    </a>
                  )
                }
                classNames="ms-0"
                component={renderCheckbox}
                validate={[required]}
              />
            </div>
          </React.Fragment>
        )}
        <div className="buttons-container">
          <button
            className="button"
            type="submit"
            disabled={submitting || !vacancy}
          >
            Создать
          </button>
          <LinkContainer
            to={cancelUrl}
          >
            <button type="button" className="button button_color_dark-blue">Отмена</button>
          </LinkContainer>
        </div>
        {submitFailed && <div className="form-error">Не все поля заполнены корректно</div>}
        <ErrorMessage errorName="createResponseError" />
      </form>
    );
  }
}

CreateResponseForm.defaultProps = {
  vacancies: [],
  activeTeamShortId: '',
};

CreateResponseForm.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  sources: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  activeTeamShortId: PropTypes.string,
  vacancies: PropTypes.arrayOf(PropTypes.object),
  isAllVacancyPage: PropTypes.bool.isRequired,
  policyUrl: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  const {
    app: { activeTeamShortId, activeTeam },
    sources,
    entities: { teams },
  } = state;
  const selector = formValueSelector('createResponse');
  const dirty = isDirty('createResponse')(state);
  const questionsData = selector(state, 'questions') || [];

  const policyUrl = teams[activeTeam] && teams[activeTeam].policy_url_not_empty;
  return {
    policyUrl,
    dirty,
    questionsData,
    activeTeamShortId,
    sources,
  };
};


export default compose(
  connect(mapStateToProps),
  reduxForm({
    form: 'createResponse',
    onChange: (values, dispatch, props) => {
      if (props.submitFailed) {
        dispatch(stopSubmit('createResponse'));
      }
    },
  }),
)(CreateResponseForm);
